"use client"

import { useCallback, useRef, useState } from "react"
import { useDropzone } from "react-dropzone"
import { Controller, FieldPath, FieldValues, useFormContext } from "react-hook-form"
import { safeFilesFromEvent } from "../../utils/safeFilesFromEvent"
import { TEditorIconButton } from "../../ui/button/TEditorIconButton"
import { TTextButton } from "../../ui/button/TTextButton"
import { TLoader } from "../../ui/display/TLoader"
import { TCounter } from "../../ui/field/TCounter"
import { TSwitch } from "../../ui/input/TSwitch"
import { TTextareaControlled } from "../../ui/input/TTextareaControlled"
import { FormControl, FormItem, FormMessage } from "../../ui/shadcn/form"
import { useToast } from "../../ui/toast/TToastProvider"
import { TTooltip } from "../../ui/tooltip/TTooltip"
import { DynamicFormLabel } from "../components/form-label"
import { ImageUploadMenu } from "../components/image-upload-menu"
import type { ParsedField } from "../types"

interface PromptFieldProps {
	field: ParsedField
}

export function PromptField({ field: parsedField }: PromptFieldProps) {
	const { name, schema, required } = parsedField
	const description = schema.description
	const title = schema.title
	const { control } = useFormContext()
	const toast = useToast()
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const fieldRef = useRef<{ onChange: (value: string) => void } | null>(null)
	const [isFocused, setIsFocused] = useState(false)
	const [isUploading, setIsUploading] = useState(false)

	// Extended UI options
	const maxChars = (schema["x-ui-max-chars"] as number) || schema.maxLength || 5000
	const spellCheck = schema["x-ui-spell-check"] !== false // default true
	const showCounter = schema["x-ui-show-counter"] !== false // default true
	const enhanceField = schema["x-ui-enhance-field"]
	const showRandomButton = schema["x-ui-show-random"] === true
	const onRandomPrompt = schema["x-ui-on-random"]
	const onFileUpload = schema["x-ui-on-file-upload"]
	const onWorkHistorySelect = schema["x-ui-on-work-history-select"]
	const onFocus = schema["x-ui-on-focus"]

	const handleFocus = useCallback(() => {
		setIsFocused(true)
		onFocus?.()
	}, [onFocus])

	const handleBlur = useCallback(() => {
		setIsFocused(false)
	}, [])

	const handleRandomClick = useCallback(
		async (field: { onChange: (value: string) => void }) => {
			if (!onRandomPrompt) return

			setIsUploading(true)
			try {
				const randomText = await onRandomPrompt()
				if (randomText) {
					field.onChange(randomText)
					textareaRef.current?.focus()
				}
			} catch (error) {
				console.error("Random prompt generation failed:", error)
				toast.error("Failed to generate random prompt")
			} finally {
				setIsUploading(false)
			}
		},
		[onRandomPrompt, toast]
	)

	const handleFileSelected = useCallback(
		async (file: File, field: { onChange: (value: string) => void }) => {
			if (!onFileUpload) return

			setIsUploading(true)
			try {
				const result = await onFileUpload(file)
				if (result && typeof result === "string") {
					toast.success("Loaded successfully")
					field.onChange(result)
					textareaRef.current?.focus()
				}
			} catch (error) {
				console.error("File upload failed:", error)
				toast.error(error instanceof Error ? error.message : "Image to Text is Failed. Please enter a prompt or retry.")
			} finally {
				setIsUploading(false)
			}
		},
		[onFileUpload, toast]
	)

	const handleWorkHistoryClick = useCallback(
		async (field: { onChange: (value: string) => void }) => {
			if (!onWorkHistorySelect || !onFileUpload) return

			setIsUploading(true)
			try {
				const imageUrls = await onWorkHistorySelect()
				if (imageUrls.length > 0) {
					const firstImageUrl = imageUrls[0]
					const response = await fetch(firstImageUrl as string)
					const blob = await response.blob()
					const file = new File([blob], "work-history-image.jpg", { type: blob.type })

					const result = await onFileUpload(file)
					if (result && typeof result === "string") {
						toast.success("Loaded successfully")
						field.onChange(result)
						textareaRef.current?.focus()
					}
				}
			} catch (error) {
				console.error("Work history selection failed:", error)
				toast.error(error instanceof Error ? error.message : "Failed to load image from work history")
			} finally {
				setIsUploading(false)
			}
		},
		[onWorkHistorySelect, onFileUpload, toast]
	)

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			if (!onFileUpload || acceptedFiles.length === 0 || !fieldRef.current) return
			handleFileSelected(acceptedFiles[0] as File, fieldRef.current)
		},
		[handleFileSelected, onFileUpload]
	)

	const { getRootProps, getInputProps, open } = useDropzone({
		// 임베디드 웹뷰의 FS 핸들 거부(NotAllowedError) 드롭 폴백 — safeFilesFromEvent 주석 참조.
		getFilesFromEvent: safeFilesFromEvent,
		onDrop,
		accept: {
			"image/*": [".png", ".jpg", ".jpeg", ".webp", ".gif"]
		},
		disabled: isUploading || !onFileUpload,
		noClick: true,
		noDrag: !onFileUpload,
		multiple: false
	})

	return (
		<div className="relative h-[180px]">
			<Controller
				name={name as FieldPath<FieldValues>}
				control={control}
				render={({ field, fieldState }) => {
					// Store field ref for dropzone access
					fieldRef.current = field

					const value = field.value || ""
					const showPlaceholder = !value && !isFocused

					return (
						<FormItem className="h-full">
							<FormControl>
								<div {...getRootProps()} className="relative h-full group">
									<input {...getInputProps()} />
									<TTextareaControlled
										label={<DynamicFormLabel name={name} title={title} required={required} description={description} />}
										name={name}
										value={value}
										onChange={field.onChange}
										onFocus={handleFocus}
										onBlur={handleBlur}
										spellCheck={spellCheck}
										error={!!fieldState.error}
										disabled={isUploading}
										renderInnerContent={
											<EnhancePromptSection
												enhanceField={enhanceField}
												showCounter={showCounter}
												showRandomButton={showRandomButton}
												maxChars={maxChars}
												currentValue={value}
												isUploading={isUploading}
												onRandomClick={() => handleRandomClick(field)}
											/>
										}
										className="h-full"
									/>
									{showPlaceholder && !isUploading && (
										<div className="absolute top-[34px] left-3 w-11/12 text-base text-text-placeholder pointer-events-auto">
											Describe your idea or{" "}
											<div className="inline-flex">
												<ImageUploadMenu onDeviceUpload={open} onWorkHistoryUpload={onWorkHistorySelect ? () => handleWorkHistoryClick(field) : undefined}>
													<TTextButton color="primary" className="font-normal!">
														upload an image
													</TTextButton>
												</ImageUploadMenu>
											</div>
										</div>
									)}
									{isUploading && (
										<div className="absolute top-0 left-0 w-full h-full pointer-events-none">
											<TLoader size={24} />
										</div>
									)}
								</div>
							</FormControl>
							<FormMessage />
						</FormItem>
					)
				}}
			/>
		</div>
	)
}

interface EnhancePromptSectionProps {
	enhanceField?: string
	showCounter: boolean
	showRandomButton: boolean
	maxChars: number
	currentValue: string
	isUploading: boolean
	onRandomClick: () => void
}

function EnhancePromptSection({ enhanceField, showCounter, showRandomButton, maxChars, currentValue, isUploading, onRandomClick }: EnhancePromptSectionProps) {
	// Hook must be called before any conditional returns
	const { control } = useFormContext()

	// Early return after all hooks are called
	if (!enhanceField && !showCounter && !showRandomButton) {
		return null
	}

	return (
		<div className="w-full h-6 flex items-center justify-between space-x-1">
			<div className="flex items-center space-x-2">
				{enhanceField && (
					<Controller
						name={enhanceField}
						control={control}
						render={({ field }) => <TSwitch value={field.value ?? true} onChange={field.onChange} label="Enhance" disabled={isUploading} />}
					/>
				)}
			</div>
			<div className="flex items-center space-x-2">
				{showCounter && <TCounter value={currentValue} maxChar={maxChars} disabled={isUploading} className="text-base!" />}
				{showRandomButton && (
					<TTooltip title="Random Prompt" side="bottom">
						<TEditorIconButton name="promptRandom" size="xsmall" color="ghost" disabled={isUploading} onClick={onRandomClick} />
					</TTooltip>
				)}
			</div>
		</div>
	)
}
